import * as R from 'remeda'

import { type PathKey } from '~/apps/ninesegsAnalyzer/routes/paths'
import { type RoleName } from '~/gql/graphql'

import { type RoleNameAndCoreMember } from './types'

export const pathKey = 'brandAccountUserList' satisfies PathKey

const rolePermissionMatrix = {
  add: {
    mForce: [],
    partner: ['PARTNER_MANAGER', 'PARTNER_MANAGER_WITH_SETTING'],
    user: [
      'PARTNER_MANAGER',
      'PARTNER_MANAGER_WITH_SETTING',
      'USER_MANAGER',
      'USER_MANAGER_WITH_SETTING',
    ],
  },
  delete: {
    mForce: [
      'MFORCE',
      'MFORCE_WITH_SETTING',
      'PARTNER_MANAGER',
      'PARTNER_MANAGER_WITH_SETTING',
      'PARTNER',
      'PARTNER_WITH_SETTING',
      'USER_MANAGER',
      'USER_MANAGER_WITH_SETTING',
      'USER',
      'USER_WITH_SETTING',
    ],
    partnerManager: [
      'PARTNER',
      'PARTNER_WITH_SETTING',
      'USER_MANAGER',
      'USER_MANAGER_WITH_SETTING',
      'USER',
      'USER_WITH_SETTING',
    ],
    userManager: ['USER', 'USER_WITH_SETTING'],
  },
  edit: {
    mForce: [
      'MFORCE',
      'MFORCE_WITH_SETTING',
      'PARTNER_MANAGER',
      'PARTNER_MANAGER_WITH_SETTING',
      'PARTNER',
      'PARTNER_WITH_SETTING',
      'USER_MANAGER',
      'USER_MANAGER_WITH_SETTING',
      'USER',
      'USER_WITH_SETTING',
    ],
    partnerManager: [
      'PARTNER_MANAGER',
      'PARTNER_MANAGER_WITH_SETTING',
      'PARTNER',
      'PARTNER_WITH_SETTING',
      'USER_MANAGER',
      'USER_MANAGER_WITH_SETTING',
      'USER',
      'USER_WITH_SETTING',
    ],
    userManager: [
      'USER_MANAGER',
      'USER_MANAGER_WITH_SETTING',
      'USER',
      'USER_WITH_SETTING',
    ],
  },
} as const

export const isCoreMember = (
  userRoleName: RoleNameAndCoreMember,
): userRoleName is 'CORE_MEMBER' => userRoleName === 'CORE_MEMBER'

export const isMForce = (userRoleName: RoleNameAndCoreMember) =>
  R.isIncludedIn(userRoleName, ['MFORCE', 'MFORCE_WITH_SETTING'])

export const isPartnerManager = (userRoleName: RoleNameAndCoreMember) =>
  R.isIncludedIn(userRoleName, [
    'PARTNER_MANAGER',
    'PARTNER_MANAGER_WITH_SETTING',
  ])

export const isUserManager = (userRoleName: RoleNameAndCoreMember) =>
  R.isIncludedIn(userRoleName, ['USER_MANAGER', 'USER_MANAGER_WITH_SETTING'])

export const isPartner = (userRoleName: RoleName) =>
  R.isIncludedIn(userRoleName, ['PARTNER', 'PARTNER_WITH_SETTING'])

export const isUser = (userRoleName: RoleName) =>
  R.isIncludedIn(userRoleName, ['USER', 'USER_WITH_SETTING'])

export const hasAccessToSetting = (userRoleName: RoleName) =>
  R.isIncludedIn(userRoleName, [
    'MFORCE_WITH_SETTING',
    'PARTNER_MANAGER_WITH_SETTING',
    'PARTNER_WITH_SETTING',
    'USER_MANAGER_WITH_SETTING',
    'USER_WITH_SETTING',
  ])

export const canAddMForce = (userRoleName: RoleNameAndCoreMember) => {
  if (isCoreMember(userRoleName)) {
    return true
  }

  return R.isIncludedIn(userRoleName, rolePermissionMatrix.add.mForce)
}

export const canAddPartner = (userRoleName: RoleNameAndCoreMember) => {
  if (isCoreMember(userRoleName)) {
    return true
  }

  return R.isIncludedIn(userRoleName, rolePermissionMatrix.add.partner)
}

export const canAddUser = (userRoleName: RoleNameAndCoreMember) => {
  if (isCoreMember(userRoleName)) {
    return true
  }

  return R.isIncludedIn(userRoleName, rolePermissionMatrix.add.user)
}

export const canDeleteByMForce = (userRoleName: RoleName) =>
  R.isIncludedIn(userRoleName, rolePermissionMatrix.delete.mForce)

export const canDeleteByPartnerManager = (userRoleName: RoleName) =>
  R.isIncludedIn(userRoleName, rolePermissionMatrix.delete.partnerManager)

export const canDeleteByUserManager = (userRoleName: RoleName) =>
  R.isIncludedIn(userRoleName, rolePermissionMatrix.delete.userManager)

export const canEditByMForce = (currentUserRole: RoleName) =>
  R.isIncludedIn(currentUserRole, rolePermissionMatrix.edit.mForce)

export const canEditByPartnerManager = (currentUserRole: RoleName) =>
  R.isIncludedIn(currentUserRole, rolePermissionMatrix.edit.partnerManager)

export const canEditByUserManager = (currentUserRole: RoleName) =>
  R.isIncludedIn(currentUserRole, rolePermissionMatrix.edit.userManager)

export const canVisiblePartnerSeat = (userRoleName: RoleNameAndCoreMember) => {
  if (isCoreMember(userRoleName)) {
    return true
  }
  if (isMForce(userRoleName)) {
    return true
  }
  if (isPartnerManager(userRoleName)) {
    return true
  }
  if (isPartner(userRoleName)) {
    return true
  }
  if (isUserManager(userRoleName)) {
    return false
  }
  if (isUser(userRoleName)) {
    return false
  }

  throw new Error(`Unknown role name: ${userRoleName}`)
}

export const getRoleNameWithAccessToSetting = (
  roleName: RoleName,
): RoleName => {
  if (isMForce(roleName)) {
    return 'MFORCE_WITH_SETTING'
  }
  if (isPartnerManager(roleName)) {
    return 'PARTNER_MANAGER_WITH_SETTING'
  }
  if (isPartner(roleName)) {
    return 'PARTNER_WITH_SETTING'
  }
  if (isUserManager(roleName)) {
    return 'USER_MANAGER_WITH_SETTING'
  }
  if (isUser(roleName)) {
    return 'USER_WITH_SETTING'
  }

  throw new Error(`Unknown role name: ${roleName}`)
}

export const getRoleNameWithoutAccessToSetting = (
  roleName: RoleName,
): RoleName => {
  if (isMForce(roleName)) {
    return 'MFORCE'
  }
  if (isPartnerManager(roleName)) {
    return 'PARTNER_MANAGER'
  }
  if (isPartner(roleName)) {
    return 'PARTNER'
  }
  if (isUserManager(roleName)) {
    return 'USER_MANAGER'
  }
  if (isUser(roleName)) {
    return 'USER'
  }

  throw new Error(`Unknown role name: ${roleName}`)
}
